import { useMemo } from "react";
import {
  CartesianGrid, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis,
} from "recharts";
import { CockpitPanel } from "@/cockpit/CockpitPanel";
import { FixedChart } from "@/cockpit/FixedChart";
import { AXIS, GRID, INFLOW, OUTFLOW, compactShares, compactUsd, fmtMd, tooltipBox } from "@/cockpit/formatCockpit";
import { BuyMark, SellMark } from "@/cockpit/chartMarks";

const CHART_H = 240;

export function InsiderScatterPlot({ activity, buyCount30d, sellCount30d, onOpenRaw }) {
  const { buys, sells, buyCount, sellCount, buyUsd, sellUsd, useLog } = useMemo(() => {
    const rows = activity || [];
    const buys = [];
    const sells = [];
    let buyUsd = 0;
    let sellUsd = 0;
    const vals = [];
    rows.forEach((r) => {
      const t = new Date(`${String(r.date || "").slice(0, 10)}T12:00:00Z`).getTime();
      if (!Number.isFinite(t)) return;
      const valueUsd = Math.max(1, Number(r.valueUsd) || 0);
      vals.push(valueUsd);
      const pt = {
        t,
        date: r.date,
        valueUsd,
        shares: r.shares,
        insiderName: r.insiderName,
        type: r.type,
      };
      if (r.type === "Sell") {
        sells.push(pt);
        sellUsd += Number(r.valueUsd) || 0;
      } else {
        buys.push(pt);
        buyUsd += Number(r.valueUsd) || 0;
      }
    });
    const mx = vals.length ? Math.max(...vals) : 1;
    const mn = vals.length ? Math.min(...vals) : 1;
    return {
      buys,
      sells,
      buyCount: buys.length,
      sellCount: sells.length,
      buyUsd,
      sellUsd,
      useLog: mx / Math.max(1, mn) >= 50,
    };
  }, [activity]);

  const ratio = sellUsd > 0 ? buyUsd / sellUsd : buyUsd > 0 ? Infinity : 0;
  const hasMarks = buys.length + sells.length > 0;

  return (
    <CockpitPanel
      title="Corporate insider trading"
      hint="Form 4 from the house-feed snapshot (up to 8 latest prints in 30d). ▲ buy / ▼ sell. Radius = share volume."
      onOpenRaw={onOpenRaw}
      testid="insider-scatter-plot"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2" data-testid="insider-macro-tiles">
        <MacroTile label="Buy / sell $" value={Number.isFinite(ratio) ? `${ratio.toFixed(2)}×` : "n/a"} />
        <MacroTile label="▲ Buy filings" value={String(buyCount30d ?? buyCount)} tone={INFLOW} />
        <MacroTile label="▼ Sell filings" value={String(sellCount30d ?? sellCount)} tone={OUTFLOW} />
      </div>
      {hasMarks ? (
        <FixedChart height={CHART_H}>
          {(width) => (
            <ScatterChart
              width={width}
              height={CHART_H}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              onClick={onOpenRaw}
            >
              <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
              <XAxis
                dataKey="t"
                type="number"
                domain={["auto", "auto"]}
                tick={{ fill: AXIS, fontSize: 9 }}
                tickFormatter={(v) => fmtMd(new Date(v).toISOString())}
              />
              <YAxis
                dataKey="valueUsd"
                type="number"
                scale={useLog ? "log" : "linear"}
                domain={["auto", "auto"]}
                tick={{ fill: AXIS, fontSize: 9 }}
                tickFormatter={compactUsd}
                width={52}
                name="Value"
              />
              <ZAxis dataKey="shares" range={[40, 280]} />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                contentStyle={tooltipBox}
                formatter={(v, name) => {
                  if (name === "valueUsd") return [compactUsd(v), "Value"];
                  if (name === "shares") return [compactShares(v), "Shares"];
                  return [v, name];
                }}
                labelFormatter={(_, pts) => {
                  const p = pts?.[0]?.payload;
                  return p ? `${p.insiderName} · ${p.type} · ${p.date}` : "";
                }}
              />
              <Scatter name="▲ Buys" data={buys} fill={INFLOW} shape={BuyMark} isAnimationActive={false} />
              <Scatter name="▼ Sells" data={sells} fill={OUTFLOW} shape={SellMark} isAnimationActive={false} />
            </ScatterChart>
          )}
        </FixedChart>
      ) : (
        <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No Form 4 prints in this window.</p>
      )}
      {hasMarks ? (
        <div className="flex gap-3 text-[10px] mt-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>
          <span style={{ color: INFLOW }}>▲ Buy</span>
          <span style={{ color: OUTFLOW }}>▼ Sell</span>
        </div>
      ) : null}
    </CockpitPanel>
  );
}

function MacroTile({ label, value, tone }) {
  return (
    <div className="px-2 py-1.5 border" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
      <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{label}</div>
      <div className="text-[13px] font-mono-data font-medium" style={{ color: tone || "var(--hoip-text-primary)" }}>{value}</div>
    </div>
  );
}
